import React from "react";
import { Box, Skeleton } from "@mui/material";

interface CustomSkeletonProps {
  rows?: number;
  circularSize?: number;
  textHeight?: number;
  gap?: number;
  hideCircular?: boolean;
  variant?:
    | "list"
    | "form"
    | "card"
    | "user"
    | "comment"
    | "receipt-transaction"
    | "graph"
    | "stat"
    | "merchant-list";
  formFieldWidths?: (string | number)[];
}

const CustomSkeleton: React.FC<CustomSkeletonProps> = ({
  rows = 6,
  circularSize = 24,
  textHeight = 28,
  gap = 2.5,
  hideCircular = false,
  variant = "list",
  formFieldWidths = [],
}) => {
  // Mimics a stat card: small label → large value → optional subtitle row
  if (variant === "stat") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: "100%",
          width: "100%",
        }}
      >
        {/* label — heading3 → line-height-snug = 20px */}
        <Skeleton
          variant="text"
          width="55%"
          height={20}
          style={{ borderRadius: 4 }}
        />
        {/* value — bodyXLarge → line-height-normal = 24px */}
        <Skeleton
          variant="text"
          width="40%"
          height={24}
          style={{ borderRadius: 4 }}
        />
      </Box>
    );
  }

  // Mimics a merchant row: circle logo | name | trend badge | amount
  if (variant === "merchant-list") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: gap,
        }}
      >
        {[...Array(rows)].map((_, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              height: circularSize,
            }}
          >
            <Skeleton
              variant="circular"
              width={circularSize}
              height={circularSize}
              style={{ flexShrink: 0 }}
            />
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Skeleton
                variant="text"
                width={idx % 2 === 0 ? 90 : 70}
                height={textHeight}
                style={{ borderRadius: 4 }}
              />
              <Skeleton
                variant="rounded"
                width={40}
                height={textHeight}
                style={{ borderRadius: 999 }}
              />
            </Box>
            <Skeleton
              variant="text"
              width={56}
              height={textHeight}
              style={{ borderRadius: 4, flexShrink: 0 }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  if (variant === "graph") {
    // Mimics a chart: Y-axis ticks, a filled plot area, and X-axis labels.
    // Fills its parent, so size it via the wrapping container (e.g. a fixed-height box).
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            gap: 1.5,
            minHeight: 0,
          }}
        >
          {/* Y-axis ticks */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              py: 0.5,
            }}
          >
            {[...Array(5)].map((_, idx) => (
              <Skeleton key={idx} variant="text" width={32} height={12} />
            ))}
          </Box>
          {/* Plot area */}
          <Skeleton
            variant="rounded"
            height="100%"
            style={{ flex: 1, borderRadius: 12 }}
          />
        </Box>
        {/* X-axis labels (offset to clear the Y-axis column) */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pl: 5,
          }}
        >
          {[...Array(6)].map((_, idx) => (
            <Skeleton key={idx} variant="text" width={28} height={12} />
          ))}
        </Box>
      </Box>
    );
  }

  if (variant === "form") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: gap,
        }}
      >
        {[...Array(rows)].map((_, idx) => (
          <Box
            key={idx}
            sx={{
              mb: 1,
            }}
          >
            <Skeleton
              variant="rectangular"
              width={formFieldWidths[idx] || "100%"}
              height={textHeight}
              style={{ borderRadius: 6 }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  if (variant === "card") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--space-5)",
          }}
        >
          <Skeleton
            variant="text"
            width={160}
            height={30}
            style={{ borderRadius: 8 }}
          />
          <Skeleton variant="text" width={100} height={28} />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "var(--space-9)",
          }}
        >
          <Skeleton
            variant="rectangular"
            width={330}
            height={208}
            style={{ borderRadius: 16 }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "var(--space-7)",
            marginBottom: "var(--space-5)",
          }}
        >
          <Skeleton
            variant="rectangular"
            width={128}
            height={40}
            style={{ borderRadius: 32 }}
          />
          <Skeleton
            variant="rectangular"
            width={128}
            height={40}
            style={{ borderRadius: 32 }}
          />
          <Skeleton
            variant="rectangular"
            width={128}
            height={40}
            style={{ borderRadius: 32 }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-7)",
          }}
        >
          <Skeleton variant="text" width={140} height={40} />
          <Skeleton variant="text" width={128} height={40} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-7)",
          }}
        >
          <Skeleton variant="text" width={140} height={40} />
          <Skeleton variant="text" width={128} height={40} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-7)",
          }}
        >
          <Skeleton variant="text" width={140} height={40} />
          <Skeleton variant="text" width={128} height={40} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <Skeleton variant="text" width="100%" height={48} />

          <Skeleton variant="text" width="100%" height={48} />

          <Skeleton variant="text" width="100%" height={48} />
        </Box>
      </Box>
    );
  }
  if (variant === "user") {
    return (
      <>
        <CustomSkeleton rows={6} gap={3.5} variant="list" />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "var(--space-7)",
            marginTop: "var(--space-9)",
            marginBottom: "var(--space-5)",
          }}
        >
          <Skeleton
            variant="rectangular"
            width={128}
            height={40}
            style={{ borderRadius: 32 }}
          />
          <Skeleton
            variant="rectangular"
            width={128}
            height={40}
            style={{ borderRadius: 32 }}
          />
          <Skeleton
            variant="rectangular"
            width={128}
            height={40}
            style={{ borderRadius: 32 }}
          />
        </Box>
      </>
    );
  }
  if (variant === "comment") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: gap,
        }}
      >
        {[...Array(rows)].map((_, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <Skeleton variant="circular" width={32} height={32} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  flex: 1,
                }}
              >
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="20%" height={20} />
              </Box>
              <Skeleton variant="text" width="90%" height={28} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (variant === "receipt-transaction") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        {[...Array(rows)].map((_, idx) => (
          <Box key={idx} sx={{ paddingBlock: "var(--space-3)" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 1.5,
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Skeleton variant="circular" width={40} height={40} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Skeleton variant="text" width={60} height={16} />
                  <Skeleton variant="text" width={120} height={22} />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 0.5,
                }}
              >
                <Skeleton variant="text" width={80} height={22} />
                <Skeleton variant="rounded" width={72} height={28} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // Default "list" variant
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: gap,
      }}
    >
      {[...Array(rows)].map((_, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {!hideCircular && (
            <Skeleton
              variant="circular"
              width={circularSize}
              height={circularSize}
            />
          )}
          <Box
            sx={{
              flex: "1",
            }}
          >
            <Skeleton variant="text" width={"60%"} height={textHeight} />
          </Box>
          <Box>
            <Skeleton variant="text" width={200} height={24} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default CustomSkeleton;
